import { ORIGINAL_GAME } from '@archon3d/content';
import { validateGameDefinition, type BoardDef, type GameDefinition } from '@archon3d/core';
import { copyEditableGame } from './copyGame';

/** The largest file the editor reads: a game with a dozen arenas is a small part of it. */
export const MAX_IMPORT_BYTES = 1_000_000;

const isRecord = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v);

/**
 * The game in a file the author chose to import, or a board in it made into a new game
 * (editor:quality#19). Anything else is refused with an error saying what the file is instead,
 * never a parser's or a script's own message.
 */
export function readImport(text: string, bytes = text.length): GameDefinition {
  const fail = (why: string) => new Error(`Import failed: ${why}`);
  if (bytes > MAX_IMPORT_BYTES) throw fail('the file is larger than 1 MB, too large for a game.');
  let value: unknown;
  try {
    value = JSON.parse(text);
  } catch {
    throw fail('the file is not a game or a board exported from the editor.');
  }
  if (!isRecord(value)) throw fail('the file holds no game or board.');
  if (typeof value.version === 'number' && value.version > 1)
    throw fail('the file is from a newer version of the game. Reload the page and try again.');
  if (value.version === 1 && isRecord(value.game))
    throw fail('the file is a saved match, not a game. Load it from the menu’s cloud saves.');
  try {
    if (value.version === 1) return validateGameDefinition(value);
    if (value.version === undefined && Array.isArray(value.cells)) {
      const board = value as unknown as BoardDef;
      const game = copyEditableGame(ORIGINAL_GAME, String(board.name ?? 'Imported board'));
      return validateGameDefinition({
        ...game,
        id: board.id,
        name: board.name,
        board,
        cellArenas: {},
        players: { min: 2, max: Array.isArray(board.factions) ? board.factions.length : 2 },
      });
    }
  } catch (e) {
    throw fail((e as Error).message);
  }
  throw fail('the file holds no game or board.');
}
